/**
 * @voltxampere/web — `<vxa-symbol-card>` (Sprint 11).
 *
 * One Sprint 10.1 component symbol rendered into the sidebar
 * palette. Shows the inline IEC SVG, TR + EN names, and the
 * shortcut badge. Click arms the symbol — Sprint 13 will hand
 * off the armed selection to the canvas.
 *
 * Accessibility: the card *is* a `<button>`, so keyboard focus
 * + Enter/Space activation come for free. The accessible name
 * follows the active locale.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { LitElement, html, css } from "lit";
import { customElement, property } from "lit/decorators.js";
import { unsafeSVG } from "lit/directives/unsafe-svg.js";
import type { ComponentSymbol } from "@voltxampere/ui";

import { armedSymbol } from "../store/ui.js";
import { currentLocale } from "../store/locale.js";
import { SignalController } from "../lit-utils/signal-controller.js";

@customElement("vxa-symbol-card")
export class VxaSymbolCard extends LitElement {
  @property({ type: Object })
  symbol!: ComponentSymbol;

  private readonly _armed = new SignalController(this, armedSymbol);
  private readonly _locale = new SignalController(this, currentLocale);

  static override styles = css`
    :host {
      display: contents;
    }

    /* Sprint 18 D17 — yatay kart layout. mockup zenit-platform 2
     * sembolü solda 40×26 alanda, sağda yazılar (TR ad + EN ad
     * stack), en sağda klavye kısayolu. flex-direction column
     * yerine row. */
    .card {
      display: grid;
      grid-template-columns: 48px 1fr auto;
      align-items: center;
      gap: var(--s-3);
      padding: var(--s-3) var(--s-3);
      background: var(--ink);
      border: 1px solid var(--ivory-wisp);
      /* Sprint 18 D18 — hover'da sol kenarda 2px brass aksent. */
      border-left: 2px solid transparent;
      cursor: pointer;
      user-select: none;
      width: 100%;
      text-align: left;
      /* v1.0.2 ADR-0106 KOL-B-FONT-1 — button user-agent default is Arial */
      font-family: var(--font-body);
      color: inherit;
      transition:
        background var(--transition-hover),
        border-color var(--transition-hover);
    }

    .card:hover {
      background: var(--ink-2);
      border-color: var(--brass-faint);
      border-left-color: var(--brass);
    }

    .card:focus-visible {
      outline: 1px solid var(--brass);
      outline-offset: -1px;
    }

    .card[data-armed] {
      background: var(--ink-3);
      border-color: var(--brass);
      border-left-color: var(--brass-hot);
    }

    .sym {
      width: 44px;
      height: 28px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .sym svg {
      width: 40px;
      height: 26px;
    }

    .names {
      display: flex;
      flex-direction: column;
      gap: 2px;
      min-width: 0;
    }

    .name-tr {
      font-family: var(--font-display);
      font-style: italic;
      font-size: 14px;
      letter-spacing: -0.005em;
      color: var(--brass);
      line-height: 1.1;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .name-en {
      font-family: var(--font-body);
      font-style: italic;
      font-size: 11px;
      color: var(--ivory-dim);
      line-height: 1.1;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .shortcut {
      font-family: var(--font-mono);
      font-size: var(--fs-mono-tiny);
      color: var(--brass);
      border: 1px solid var(--brass-faint);
      padding: 3px 7px;
      letter-spacing: 0.18em;
    }
  `;

  private readonly _onClick = (): void => {
    armedSymbol.value = this.symbol;
    console.info(`[VXA] armed: ${this.symbol.id}`);
  };

  override render() {
    const isArmed = this._armed.value?.id === this.symbol.id;
    const aria = this._locale.value === "tr" ? this.symbol.nameTr : this.symbol.nameEn;

    return html`
      <button
        class="card"
        type="button"
        ?data-armed=${isArmed}
        aria-label=${aria}
        @click=${this._onClick}
      >
        <span class="sym">
          <svg viewBox=${this.symbol.viewBox} aria-hidden="true">
            ${unsafeSVG(this.symbol.renderSVG())}
          </svg>
        </span>
        <span class="names">
          <span class="name-tr">${this.symbol.nameTr}</span>
          <span class="name-en">${this.symbol.nameEn}</span>
        </span>
        <span class="shortcut">${this.symbol.shortcut}</span>
      </button>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "vxa-symbol-card": VxaSymbolCard;
  }
}
